import { ref, computed } from 'vue'
import { FEED_CONSTANTS, POST_POSITION } from '@/Shared/feedConstants'

/**
 * Composable for managing virtual post window
 * Only renders 3 posts at a time (previous, current, next) for performance
 *
 * @param {Ref} postsWithAds - Reference to posts array with ads inserted
 * @param {Function} emit - Vue emit function for events
 * @param {Ref} scrollLocked - Reference to scroll lock state
 * @param {Object} fetchFunctions - Object containing nextFetch and prevFetch refs
 * @param {Ref} postsData - Reference to original posts data for boundary detection
 * @param {Number} initialIndex - Initial index to start at (default 0)
 * @returns {Object} Virtual posts state and navigation function
 */
export function useVirtualPosts(postsWithAds, emit, scrollLocked, fetchFunctions, postsData, initialIndex = 0) {
    const currentIndex = ref(initialIndex)

    const virtualPosts = computed(() => {
        const posts = postsWithAds.value
        const prevIndex = currentIndex.value - 1
        const nextIndex = currentIndex.value + 1

        return [
            {
                post: prevIndex >= 0 ? posts[prevIndex] : null,
                index: prevIndex,
                position: POST_POSITION.PREVIOUS
            },
            {
                post: posts[currentIndex.value],
                index: currentIndex.value,
                position: POST_POSITION.CURRENT
            },
            {
                post: nextIndex < posts.length ? posts[nextIndex] : null,
                index: nextIndex,
                position: POST_POSITION.NEXT
            }
        ].filter(item => item.post != null)
    })

    const navigateToPost = (direction) => {
        if (scrollLocked.value) {
            return
        }

        const posts = postsWithAds.value
        const newIndex = direction === 'next'
            ? Math.min(currentIndex.value + 1, posts.length - 1)
            : Math.max(currentIndex.value - 1, 0)

        if (newIndex === currentIndex.value) {
            return
        }

        const oldPost = posts[currentIndex.value]
        emit('post-leave', oldPost, currentIndex.value)

        currentIndex.value = newIndex

        const newPost = posts[newIndex]
        emit('post-active', newPost, newIndex)
        emit('post-enter', newPost, newIndex)

        // Trigger fetching more posts when near the end
        if (direction === 'next' && newIndex >= postsData.value.data.length - FEED_CONSTANTS.LOAD_MORE_BUFFER && fetchFunctions.nextFetch.value) {
            fetchFunctions.nextFetch.value()
        }

        if (direction === 'prev' && newIndex <= 1 && fetchFunctions.prevFetch.value) {
            fetchFunctions.prevFetch.value()
        }
    }

    return {
        currentIndex,
        virtualPosts,
        navigateToPost,
    }
}
